import api, { buildURLWithParams } from '@/services/api';
import { handleException } from '@/utils/exception';
import { toast } from 'react-toastify';

import {
  type RoleDTO,
  type UserBrief,
  type UserDetails,
  type UserWithAvailableHoursDTO,
  type UserWithRolesDTO,
} from '../types';

const BASE_URL = 'users';

export const getRoles = async () => {
  return await api.get<RoleDTO[]>(`${BASE_URL}/roles`);
};

export const getUser = async (id: number) => {
  try {
    return await api.get<UserDetails>(`${BASE_URL}/${id}`);
  } catch (e) {
    toast.error(handleException(e, 'Failed to get User'));
    throw e;
  }
};

export const getUsersWithRoles = async (includeDeactivated = false) => {
  return await api.get<UserWithRolesDTO[]>(buildURLWithParams(`${BASE_URL}/with-roles`, { includeDeactivated }));
};

export const restoreUser = async (id: number) => {
  try {
    await api.post(`${BASE_URL}/${id}/restore`);
    toast.success('User restored successfully');
  } catch (e) {
    throw new Error(handleException(e, 'Failed to restore User'));
  }
};

export const getUsers = async (hasCostGroup = false) => {
  return await api.get<UserBrief[]>(buildURLWithParams(BASE_URL, { hasCostGroup }));
};

export const getUsersWithAvailableHours = async (year: number) => {
  return await api.get<UserWithAvailableHoursDTO[]>(buildURLWithParams(`${BASE_URL}/available-hours`, { year }));
};

export const getProductOwners = async () => {
  return await api.get<UserBrief[]>(`${BASE_URL}/product-owners`);
};

export const getProductManagers = async () => {
  return await api.get<UserBrief[]>(`${BASE_URL}/product-managers`);
};

export const getTechnologyManagers = async () => {
  return await api.get<UserBrief[]>(`${BASE_URL}/technology-managers`);
};

export const getIndustryManagers = async () => {
  return await api.get<UserBrief[]>(`${BASE_URL}/industry-managers`);
};

export const checkIfUserIsAnyProductManager = async () => {
  return await api.get<boolean>(`${BASE_URL}/is-any-product-manager`);
};

export const getRoadmapOwners = async () => {
  return await api.get<UserBrief[]>(`${BASE_URL}/roadmap-owners`);
};

export const getRoadmapResources = async () => {
  return await api.get<UserBrief[]>(`${BASE_URL}/roadmap-resources`);
};

export const updateUser = async (data: UserBrief) => {
  try {
    await api.put(BASE_URL, data);
    toast.success('User updated successfully');
  } catch (e) {
    toast.error(handleException(e, 'Failed to update User'));
    throw e;
  }
};

export const createUser = async (data: UserBrief) => {
  try {
    await api.post(BASE_URL, data);
    toast.success('User created successfully');
  } catch (e) {
    toast.error(handleException(e, 'Failed to create User'));
    throw e;
  }
};

export const deleteUser = async (id: number) => {
  try {
    await api.delete(`${BASE_URL}/${id}`);
    toast.success('User deleted successfully');
  } catch (e) {
    throw new Error(handleException(e, 'Failed to delete User'));
  }
};
